import { InjectionToken } from '@angular/core';

export interface RuntimeConfig {
  googleMapsApiKey: string;
  googleMapsMapId: string;
  googleAuthClientId: string;
  allowEmailLogin: boolean;
  mediaStorageUrl: string;
  mediaStorageSource: string;
  mcpServerUrl: string;
  paymentsApiUrl: string;
  apiBaseUrl: string;
}

const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  googleMapsApiKey: '',
  googleMapsMapId: 'DEMO_MAP_ID',
  googleAuthClientId: '',
  allowEmailLogin: false,
  mediaStorageUrl: 'https://storage.realty.one',
  mediaStorageSource: 'local:///',
  mcpServerUrl: '',
  paymentsApiUrl: '/payments-api/v1',
  apiBaseUrl: '',
};

let runtimeConfig: RuntimeConfig = { ...DEFAULT_RUNTIME_CONFIG };

export const RUNTIME_CONFIG = new InjectionToken<RuntimeConfig>('runtime-config', {
  factory: () => runtimeConfig,
});

export function getRuntimeConfig(): RuntimeConfig {
  return runtimeConfig;
}

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  const localConfig = shouldLoadLocalRuntimeConfig()
    ? await readConfig('/runtime-config.local.json')
    : null;
  const baseConfig = await readConfig('/runtime-config.json');

  runtimeConfig = normalizeRuntimeConfig({
    ...baseConfig,
    ...localConfig,
  });

  return runtimeConfig;
}

function shouldLoadLocalRuntimeConfig(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

async function readConfig(path: string): Promise<Partial<RuntimeConfig> | null> {
  try {
    const response = await fetch(path, { cache: 'no-store' });
    if (!response.ok) {
      return null;
    }

    const payload = (await response.json()) as Partial<RuntimeConfig>;
    return payload;
  } catch {
    return null;
  }
}

function normalizeRuntimeConfig(raw: Partial<RuntimeConfig> | null): RuntimeConfig {
  return {
    googleMapsApiKey: normalizeText(raw?.googleMapsApiKey),
    googleMapsMapId: normalizeText(raw?.googleMapsMapId) || DEFAULT_RUNTIME_CONFIG.googleMapsMapId,
    googleAuthClientId: normalizeText(raw?.googleAuthClientId),
    allowEmailLogin: normalizeBoolean(raw?.allowEmailLogin, DEFAULT_RUNTIME_CONFIG.allowEmailLogin),
    mediaStorageUrl: normalizeUrl(raw?.mediaStorageUrl) || DEFAULT_RUNTIME_CONFIG.mediaStorageUrl,
    mediaStorageSource: normalizeMediaStorageSource(raw?.mediaStorageSource),
    mcpServerUrl: normalizeUrl(raw?.mcpServerUrl),
    paymentsApiUrl: normalizeUrl(raw?.paymentsApiUrl) || DEFAULT_RUNTIME_CONFIG.paymentsApiUrl,
    apiBaseUrl: normalizeUrl(raw?.apiBaseUrl),
  };
}

function normalizeText(value: string | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeBoolean(value: boolean | string | undefined, fallback: boolean): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 'yes', 'on'].includes(normalized)) {
      return true;
    }
    if (['0', 'false', 'no', 'off'].includes(normalized)) {
      return false;
    }
  }

  return fallback;
}

function normalizeUrl(value: string | undefined): string {
  return normalizeText(value).replace(/\/+$/, '');
}

function normalizeMediaStorageSource(value: string | undefined): string {
  const source = normalizeText(value) || DEFAULT_RUNTIME_CONFIG.mediaStorageSource;
  return source.endsWith('/') ? source : `${source}/`;
}
